import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { consumeEmbedHandoff } from './embedTokenHandoff';
import { fetchOrgFeatures } from './api';
import { analogueFlags } from './orgFeatures';

const AuthContext = createContext(null);

const TOKEN_KEY = 'tachoparser_token';
const USER_KEY = 'tachoparser_user';

/* No optional features: what a signed-out session has, and what a failed
   lookup means. One frozen object so re-setting it is a no-op React can
   bail out of rather than another render. */
const NO_FEATURES = Object.freeze({});

export function AuthProvider({ children }) {
  // DDIR iframe handoff: if the page was loaded via /embed/<page> the
  // backend redirected here with the session JWT in the URL fragment
  // (see api/server.py embed_entry + lib/embedTokenHandoff.js). Consume
  // BEFORE the localStorage read below so the freshly-handed-off token
  // wins over any stale token from a previous embed session in the
  // same iframe. consumeEmbedHandoff is a no-op if the fragment is
  // absent, so non-embed page loads (operators logging in directly at
  // tacho.ddir.co.uk) are unaffected.
  const handoffToken = typeof window !== 'undefined' ? consumeEmbedHandoff(window) : null;
  const [token, setToken] = useState(() => handoffToken || localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem(USER_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  /* The org's optional features (GET /api/org/features). {} means the org
     has none, which is also what a failed lookup means: an entitlement is
     the server's to grant, and an org that cannot be asked must not be
     offered a screen that 404s on its first click. */
  const [features, setFeatures] = useState(NO_FEATURES);
  /* Whether the features on hand are the server's answer for the org now
     selected. Until they are, nothing about an optional feature may be said
     either way: no screen says the reader is switched off before the server
     has said so (lib orgFeatures). */
  const [featuresLoaded, setFeaturesLoaded] = useState(false);

  // On mount, validate token by fetching /api/auth/me
  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    fetch('/api/auth/me', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Invalid token');
        return res.json();
      })
      .then((data) => {
        const userData = {
          id: data.id,
          email: data.email,
          display_name: data.display_name,
          is_super_admin: data.is_super_admin,
          org_id: data.current_org_id,
          role: data.current_role,
          memberships: data.memberships || [],
        };
        setUser(userData);
        localStorage.setItem(USER_KEY, JSON.stringify(userData));
      })
      .catch(() => {
        // Token invalid — clear auth
        setToken(null);
        setUser(null);
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
      })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* Read the org's features once the session has resolved, and again
     whenever the org changes — a super admin switching org is switching
     entitlements with it. Deliberately AFTER `loading` clears, so the
     token has already been validated by /api/auth/me above and a stale
     one bounces to the login screen there rather than through the shared
     request helper's 401-and-reload path. */
  const orgId = user?.org_id || '';
  useEffect(() => {
    if (loading || !token || !user) {
      setFeatures(NO_FEATURES);
      setFeaturesLoaded(false);
      return undefined;
    }
    let cancelled = false;
    /* Nothing is known of this org's features until it answers. */
    setFeaturesLoaded(false);
    fetchOrgFeatures().then((f) => {
      if (!cancelled) {
        setFeatures(f);
        setFeaturesLoaded(true);
      }
    });
    return () => { cancelled = true; };
  }, [loading, token, orgId]); // eslint-disable-line react-hooks/exhaustive-deps

  const login = useCallback(async (email, password) => {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.detail || 'Login failed');
    }

    const data = await res.json();
    const userData = {
      id: data.user.id,
      email: data.user.email,
      display_name: data.user.display_name,
      is_super_admin: data.user.is_super_admin,
      org_id: data.user.org_id,
      org_name: data.user.org_name,
      role: data.user.role,
      memberships: [],
    };

    setToken(data.token);
    setUser(userData);
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(userData));

    // Fetch full memberships
    try {
      const meRes = await fetch('/api/auth/me', {
        headers: { Authorization: `Bearer ${data.token}` },
      });
      if (meRes.ok) {
        const me = await meRes.json();
        userData.memberships = me.memberships || [];
        setUser({ ...userData });
        localStorage.setItem(USER_KEY, JSON.stringify(userData));
      }
    } catch {
      // Non-critical
    }

    return userData;
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }, []);

  const switchOrg = useCallback(
    async (organisationId) => {
      const res = await fetch('/api/auth/switch-org', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ organisation_id: organisationId }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || 'Failed to switch organisation');
      }

      const data = await res.json();
      const updatedUser = {
        ...user,
        org_id: data.org_id,
        org_name: data.org_name,
        role: data.role,
      };

      setToken(data.token);
      setUser(updatedUser);
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(USER_KEY, JSON.stringify(updatedUser));

      return updatedUser;
    },
    [token, user],
  );

  const value = {
    token,
    user,
    loading,
    features,
    featuresLoaded,
    /* The chart reader is an ENTITLEMENT, billed per disc, so its flags are
       only ever what the server said — never a default, never a guess (lib
       orgFeatures): analogueEnabled, the server's own true;
       analogueSwitchedOff, its own false once it has answered;
       analogueUnknown, an answer with neither (a failed lookup); and
       analogueHasCharts, charts on file, the reader on or off, so an org
       switched off keeps the screen for the discs it already has
       (design 2026-10-06). */
    ...analogueFlags(features, featuresLoaded),
    isAuthenticated: !!token && !!user,
    isAdmin: user?.role === 'admin' || user?.is_super_admin,
    isSuperAdmin: user?.is_super_admin,
    login,
    logout,
    switchOrg,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
